<template>
  <div>
    <h3 class="display-4" @click="enableDebug($event)">Manage File Uploads</h3>

    <o-list-toolbar
      :filters-list="filter_list"
      :search-fields-list="search_field_list"
      :selected-filter="selected_filter"
      :list-options="list_options"
      @reset_table="reset_table"
      @options_changed="update_table($event)"
      @filter_changed="update_filter($event)"
      :schema="schema"
      :query-select-options="options"
    >
      <template #middle>
        <o-list-multi-actions
          :schema="schema"
          :selection="selected"
          @delete="doDeleteItems"
          @merge="doMergeItems"
          enable-delete
          enable-merge
        />
      </template>
    </o-list-toolbar>

    <BTable
      striped
      bordered
      :fields="fields"
      :items="items"
      head-variant="light"
      class="o-list"
      thead-class="o-gradient-header"
      @row-selected="onRowSelected"
      :selectable="selected.selectable"
      :key="selected.selectable"
      :busy="working"
      no-local-sorting
      @sort-changed="change_sort"
      :sort-by="list_options.sort"
      :sort-desc="list_options.order === 'desc'"
    >
      <template v-slot:cell(_selected)="{ rowSelected }">
        <template v-if="rowSelected">
          <span aria-hidden="true">&check;</span>
          <span class="sr-only">Selected</span>
        </template>
        <template v-else>
          <span aria-hidden="true">&nbsp;</span>
          <span class="sr-only">Not selected</span>
        </template>
      </template>
      <template #top-row="{ columns, fields }" v-if="filterRow">
        <filter-cell
          :key="field.key"
          v-for="field in fields"
          :field="field"
          :schema="schemaModel"
          :options="filter_row_options"
          :qbe="list_options.qbe"
          @filter="filter_changed"
        />
      </template>
      <template #cell(_display_order)="{ index, item }">
        <o-link-cell
          :to="{ name: 'file_upload.edit', params: { id: item.id } }"
          :when="first_column_key === '_display_order'"
        >
          <number-display :value="item._display_order" :decimals="0" />
        </o-link-cell>
      </template>
      <template #cell(_is_deleted)="{ index, item }">
        <o-link-cell
          :to="{ name: 'file_upload.edit', params: { id: item.id } }"
          :when="first_column_key === '_is_deleted'"
        >
          <boolean-display :value="item._is_deleted"></boolean-display>
        </o-link-cell>
      </template>
      <template #cell(content_type)="{ index, item }">
        <o-link-cell
          :to="{ name: 'file_upload.edit', params: { id: item.id } }"
          :when="first_column_key === 'content_type'"
        >
          <default-display
            :value="item.content_type"
            default-value="-"
          ></default-display>
        </o-link-cell>
      </template>
      <template #cell(created_date)="{ index, item }">
        <o-link-cell
          :to="{ name: 'file_upload.edit', params: { id: item.id } }"
          :when="first_column_key === 'created_date'"
        >
          <date-display
            :value="item.created_date"
            format="DD/MM/YYYY hh:mm"
          ></date-display>
        </o-link-cell>
      </template>
      <template #cell(file)="{ index, item }">
        <o-link-cell
          :to="{ name: 'file_upload.edit', params: { id: item.id } }"
          :when="first_column_key === 'file'"
        >
          <default-display
            :value="item.file"
            default-value="-"
          ></default-display>
        </o-link-cell>
      </template>
      <template #cell(file_size)="{ index, item }">
        <o-link-cell
          :to="{ name: 'file_upload.edit', params: { id: item.id } }"
          :when="first_column_key === 'file_size'"
        >
          <number-display :value="item.file_size" :decimals="2" />
        </o-link-cell>
      </template>
      <template #cell(file_type)="{ index, item }">
        <o-link-cell
          :to="{ name: 'file_upload.edit', params: { id: item.id } }"
          :when="first_column_key === 'file_type'"
        >
          <lookup-display
            :list="optionCodeFileTypeItemsById"
            :value="item.file_type"
            display-field="description"
            default-value="-"
          ></lookup-display>
        </o-link-cell>
      </template>
      <template #cell(modified_date)="{ index, item }">
        <o-link-cell
          :to="{ name: 'file_upload.edit', params: { id: item.id } }"
          :when="first_column_key === 'modified_date'"
        >
          <date-display
            :value="item.modified_date"
            format="DD/MM/YYYY hh:mm"
          ></date-display>
        </o-link-cell>
      </template>
      <template #cell(name)="{ index, item }">
        <o-link-cell
          :to="{ name: 'file_upload.edit', params: { id: item.id } }"
          :when="first_column_key === 'name'"
        >
          <div :id="'popover-target-' + item.id">
            <default-display
              :value="item.name"
              default-value="-"
            ></default-display>
          </div>

          <b-popover
            :target="'popover-target-' + item.id"
            triggers="hover"
            placement="top"
          >
            <template #title>Preview</template>
            <preview :item="item"></preview>
          </b-popover>
        </o-link-cell>
      </template>
      <template #cell(parent_id)="{ index, item }">
        <o-link-cell
          :to="{ name: 'file_upload.edit', params: { id: item.id } }"
          :when="first_column_key === 'parent_id'"
        >
          <number-display :value="item.parent_id" :decimals="0" />
          <div v-if="item.parent_id === null || item.parent_id === 0">
            <b-input v-model="newParentID[item.id]"></b-input>
            <b-button @click="updateParentID(item.id, item)">Update</b-button>
          </div>
        </o-link-cell>
      </template>
      <template #cell(parent_type)="{ index, item }">
        <o-link-cell
          :to="{ name: 'file_upload.edit', params: { id: item.id } }"
          :when="first_column_key === 'parent_type'"
        >
          <default-display
            :value="item.parent_type"
            default-value="-"
          ></default-display>
        </o-link-cell>
      </template>
      <template #cell(url_guid)="{ index, item }">
        <o-link-cell
          :to="{ name: 'file_upload.edit', params: { id: item.id } }"
          :when="first_column_key === 'url_guid'"
        >
          <default-display
            :value="item.url_guid"
            default-value="-"
          ></default-display>
        </o-link-cell>
      </template>
      <template #cell(when_uploaded)="{ index, item }">
        <o-link-cell
          :to="{ name: 'file_upload.edit', params: { id: item.id } }"
          :when="first_column_key === 'when_uploaded'"
        >
          <date-display
            :value="item.when_uploaded"
            format="DD/MM/YYYY hh:mm"
          ></date-display>
        </o-link-cell>
      </template>
      <template #cell(who_uploaded)="{ index, item }">
        <o-link-cell
          :to="{ name: 'file_upload.edit', params: { id: item.id } }"
          :when="first_column_key === 'who_uploaded'"
        >
          <lookup-display
            :list="optionUserItemsById"
            :value="item.who_uploaded"
            display-field="description"
            default-value="-"
          ></lookup-display>
        </o-link-cell>
      </template>

      <template slot="empty">
        <p>
          <emoticon-sad-icon large></emoticon-sad-icon>
        </p>
        No Data Found. Please
        <router-link :to="{ name: 'file_upload.create' }"
          >create a new item</router-link
        >
      </template>
    </BTable>

    <o-list-paging
      :limit="list_options.limit"
      :page-options="rows_per_page_items"
      :offset="list_options.offset"
      :total-items="total_items"
      @change-limit="change_limit"
      @change-page="change_page"
    />
  </div>
</template>

<style></style>

<script>
import { BTable } from "bootstrap-vue";
import {
  CreateCodeTableFetchMixin,
  CreateGroupPermissionMixin,
  CreateListPageMixin,
  MultiActionsMixin,
} from "@/vue-ocom/mixins";
import FetchAndUseCodeModMixin from "@/vue-ocom/mixins/FetchAndUseCodeModMixin";
import {
  fieldToTableColumn,
  GetSearchFields,
} from "@/vue-ocom/schema/schema_utils";
import { cleanUpModel } from "@/vue-ocom/schema/schema_vuetify.js";
import schema from "../../schema/index.js";
import { mapActions, mapMutations } from "vuex";

import OListToolbar from "@/vue-ocom/components/list/OListToolbar.vue";
import OListPaging from "@/vue-ocom/components/list/OListPaging.vue";
import DefaultDisplay from "@/vue-ocom/components/display/DefaultDisplay.vue";
import DateDisplay from "@/vue-ocom/components/display/DateDisplay.vue";
import NumberDisplay from "@/vue-ocom/components/display/NumberDisplay.vue";
import BooleanDisplay from "@/vue-ocom/components/display/BooleanDisplay.vue";
import LookupDisplay from "@/vue-ocom/components/display/LookupDisplay.vue";
import EmoticonSadIcon from "vue-material-design-icons/EmoticonSad.vue";
import EmailIcon from "vue-material-design-icons/Email.vue";
import LinkIcon from "vue-material-design-icons/Link.vue";
import FilterCell from "@/vue-ocom/components/list/filter/FilterCell.vue";
import Preview from "../components/Preview.vue";

export default {
  components: {
    BTable,
    EmoticonSadIcon,
    EmailIcon,
    LinkIcon,
    OListPaging,
    OListToolbar,
    DateDisplay,
    DefaultDisplay,
    BooleanDisplay,
    NumberDisplay,
    LookupDisplay,
    FilterCell,
    Preview,
  },
  mixins: [
    CreateListPageMixin("FileUpload2", "file_upload", "unlinked"),
    MultiActionsMixin,
    CreateGroupPermissionMixin("FileUpload"),
    CreateCodeTableFetchMixin(["User", "CodeFileType"]),
  ],
  data: function () {
    return {
      schemaModel: cleanUpModel(
        schema.FileUpload,
        {}
      ) /*Use this.schema instead */,

      search_field_list: GetSearchFields(schema.FileUpload),

      filterRow: true /* Set to False to disable filter row */,
      filter_list: [
        {
          name: "Unlinked",
          code: "unlinked",
          options: { offset: 0, sort: "id" },
        },
        { name: "All", code: "all", options: { offset: 0, sort: "id" } },
      ],

      user_column_selection: null /* If user can customise the columns set them here */,
      column_override_params: {
        /* Put any column overrides here key by fieldname */
      },
      newParentID: [],
    };
  },
  methods: {
    ...mapActions("FileUploadModule", ["patchItem"]),
    ...mapMutations(["showMessage"]),
    updateParentID(id, item) {
      var new_parent_id = this.newParentID[id];
      var payload;

      if (new_parent_id) {
        payload = {
          id: id,
          parent_id: new_parent_id,
        };

        this.patchItem(payload)
          .then(() => {
            this.showMessage({
              message: "Success",
              type: "success",
              show: true,
            });
            item.parent_id = new_parent_id; // fake it so Input Disappears
          })
          .catch(() => {
            this.showMessage({
              message: "Error",
              type: "danger",
              show: true,
            });
          });
      }
    },
  },
  computed: {
    options() {
      return {
        User: {
          options: this.optionUserItems,
          textField: "email",
          valueField: "id",
        },
        CodeFileType: {
          options: this.optionCodeFileTypeItems,
          textField: "description",
          valueField: "id",
        },
      };
    },
    fields() {
      const vm = this;
      const selectedColumns = this.user_column_selection || [
        "name",
        "who_uploaded",
        "when_uploaded",
        "file_type",
        "parent_type",
        "parent_id",
        "_is_deleted",
        "created_date",
        "modified_date",
      ];
      const fields = selectedColumns.map(function (item) {
        return fieldToTableColumn(
          vm.schema,
          item,
          vm.column_override_params[item]
        );
      });

      // fields.push({ key: "Preview", label: "Preview", sortable: false });

      return this.addSelectionColums(
        this.filterTableColumns(this.schema, fields)
      );
    },
    first_column_key() {
      if (this.fields) {
        return this.fields[0].key;
      } else {
        return null;
      }
    },
    filter_row_options() {
      // Options for the FilterRow components
      return {
        id: false /*No filter for this field */,
        modified_date: {},
        created_date: {},
        _is_deleted: {},
        _display_order: {},
        parent_id: {},
        parent_type: {},
        file: {},
        name: {},
        content_type: {},
        who_uploaded: {
          options: this.optionUserItems,
          loading: this.optionUserWorking,
          "text-field": "email",
          "value-field": "id",
          "sort-field": "email",
          "blank-option-text": "",
        },
        when_uploaded: {},
        file_type: {
          options: this.optionCodeFileTypeItems,
          loading: this.optionCodeFileTypeWorking,
          "text-field": "description",
          "value-field": "id",
          "sort-field": "description",
          "blank-option-text": "",
        },
        url_guid: {},
        file_size: {},
      };
    },
  },
};
</script>
